import { fade } from 'svelte/transition';
import type { FadeParams, TransitionConfig } from 'svelte/transition';
import { cubicOut } from 'svelte/easing';

/** Snappy promo dismiss / appear timings. */
export const PROMO_FADE_OUT_MS = 180;
export const PROMO_FADE_IN_MS = 200;
export const PROMO_COLLAPSE_MS = 220;

/** RGB ↔ WW ControlCard: fade sliders out, then shrink; grow first, then fade sliders in. */
export const LIGHT_MODE_RAIL_FADE_MS = 180;
export const LIGHT_MODE_RAIL_HEIGHT_MS = 280;

/** Shared easing for width/height collapses. */
export const PROMO_COLLAPSE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export type UiTransitionType = 'fade' | 'collapse' | 'fadeCollapse';

export const prefersReducedMotion = (): boolean => {
	try {
		return (
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
		);
	} catch {
		return false;
	}
};

const motionDuration = (ms: number): number => (prefersReducedMotion() ? 0 : ms);

/** Fade with promo defaults (override via params). */
export const promoFade = (node: Element, params: FadeParams = {}): TransitionConfig => {
	const duration = motionDuration(params.duration ?? PROMO_FADE_OUT_MS);
	return fade(node, { ...params, duration });
};

type CollapseParams = {
	delay?: number;
	duration?: number;
	easing?: (t: number) => number;
	/** When false, only height collapses (no opacity). Default true. */
	opacity?: boolean;
};

/**
 * Collapse height (and optionally opacity) for container outro/intro.
 * Measures current height so the strip/widget shrinks top → bottom,
 * letting content below (lists) reflow smoothly into the gap.
 */
export const promoCollapse = (
	node: Element,
	{ delay = 0, duration = PROMO_COLLAPSE_MS, easing = cubicOut, opacity = true }: CollapseParams = {},
): TransitionConfig => {
	const style = getComputedStyle(node);
	const opacityValue = +style.opacity;
	// Prefer offsetHeight — `height: auto` parses poorly from computed style.
	const height = node instanceof HTMLElement ? node.offsetHeight : parseFloat(style.height) || 0;
	const paddingTop = parseFloat(style.paddingTop) || 0;
	const paddingBottom = parseFloat(style.paddingBottom) || 0;
	const marginTop = parseFloat(style.marginTop) || 0;
	const marginBottom = parseFloat(style.marginBottom) || 0;
	const borderTopWidth = parseFloat(style.borderTopWidth) || 0;
	const borderBottomWidth = parseFloat(style.borderBottomWidth) || 0;

	const ms = motionDuration(duration);

	return {
		delay,
		duration: ms,
		easing,
		css: (t) => {
			const o = opacity ? (t * opacityValue).toString() : style.opacity;
			return (
				`overflow: hidden;` +
				`opacity: ${o};` +
				`height: ${t * height}px;` +
				`padding-top: ${t * paddingTop}px;` +
				`padding-bottom: ${t * paddingBottom}px;` +
				`margin-top: ${t * marginTop}px;` +
				`margin-bottom: ${t * marginBottom}px;` +
				`border-top-width: ${t * borderTopWidth}px;` +
				`border-bottom-width: ${t * borderBottomWidth}px;`
			);
		},
	};
};

/** Collapse with fade (default for promo containers). */
export const promoFadeCollapse = (node: Element, params: CollapseParams = {}): TransitionConfig =>
	promoCollapse(node, { opacity: true, ...params });

/**
 * For `{#if}` show/hide that must ease the vertical gap closed for siblings below,
 * use `CollapseGap` (`src/components/Utility/CollapseGap.svelte`) — it height-locks
 * before unmount and runs `promoFadeCollapse` on the host.
 */
